/**
 * Active Scans Hook - Track in-progress scans across the app
 *
 * Persists queued/running scans to localStorage so they survive page refreshes.
 * Polls for status updates every 5 s. When a scan completes it is removed and
 * report queries are invalidated so lists auto-refresh.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { apiClient, getStoredToken, cancelAuthFailure } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { REPORTS_QUERY_KEYS } from '@/hooks/useReports';
import { toast } from '@/hooks/useToast';

export interface ActiveScan {
  scanId: string;
  target: string;
  branch?: string;
  targetType: string;
  status: 'queued' | 'running';
  createdAt: string;
  estimatedDuration?: number;
}

export const ACTIVE_SCANS_KEY = ['active-scans'] as const;
const STORAGE_KEY = 'guardrail_active_scans';

/** Shape returned per-scan by the poller tick. */
interface PollOutcome {
  scan: ActiveScan;
  nextState: ActiveScan | null; // null = finished/terminal
  finalStatus: 'completed' | 'failed' | null;
}

// Module-level singleton — prevents duplicate intervals when the hook
// is mounted in more than one component at the same time.
let _pollInterval: ReturnType<typeof setInterval> | null = null;
let _pollCount = 0;

/**
 * Scan IDs that the getScan poller has confirmed as terminal (completed/failed).
 * Populated before the scan is evicted from the active queue so that the
 * ScanHistoryPage reconcile effect does not re-add them while the /v2/reports
 * Prisma row is still catching up. Entries are kept for 30 s (well beyond
 * any KV/Prisma propagation lag) then cleared to avoid unbounded growth.
 */
const _confirmedTerminal = new Map<string, number>(); // scanId → evictedAt ms
const TERMINAL_TTL_MS = 30_000;

/** Mark a scan as confirmed-terminal. Called by the poller before eviction. */
export function markScanTerminal(scanId: string): void {
  _confirmedTerminal.set(scanId, Date.now());
  // Prune stale entries
  const cutoff = Date.now() - TERMINAL_TTL_MS;
  for (const [id, ts] of _confirmedTerminal) {
    if (ts < cutoff) _confirmedTerminal.delete(id);
  }
}

/** Returns true if the poller has confirmed this scan is terminal. */
export function isScanConfirmedTerminal(scanId: string): boolean {
  const ts = _confirmedTerminal.get(scanId);
  if (!ts) return false;
  if (Date.now() - ts > TERMINAL_TTL_MS) {
    _confirmedTerminal.delete(scanId);
    return false;
  }
  return true;
}

function resolveAuthToken(contextToken: string | null | undefined): string | null {
  return contextToken || getStoredToken() || null;
}

function loadFromStorage(): ActiveScan[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ActiveScan[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(scans: ActiveScan[]): void {
  if (scans.length === 0) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scans));
  }
}

export function useActiveScans() {
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: activeScans = [] } = useQuery<ActiveScan[]>({
    queryKey: ACTIVE_SCANS_KEY,
    queryFn: () => {
      // Merge cache + storage so a refresh restores in-flight scans
      const cached = queryClient.getQueryData<ActiveScan[]>(ACTIVE_SCANS_KEY) || [];
      const stored = loadFromStorage();
      const merged = [...cached];
      for (const s of stored) {
        if (!merged.some((c) => c.scanId === s.scanId)) merged.push(s);
      }
      return merged;
    },
    initialData: loadFromStorage,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const addScan = useCallback(
    (scan: ActiveScan) => {
      queryClient.setQueryData<ActiveScan[]>(ACTIVE_SCANS_KEY, (old = []) => {
        if (old.some((s) => s.scanId === scan.scanId)) return old;
        const next = [scan, ...old];
        saveToStorage(next);
        return next;
      });
    },
    [queryClient]
  );

  const removeScan = useCallback(
    (scanId: string) => {
      queryClient.setQueryData<ActiveScan[]>(ACTIVE_SCANS_KEY, (old = []) => {
        const next = old.filter((s) => s.scanId !== scanId);
        saveToStorage(next);
        return next;
      });
    },
    [queryClient]
  );

  const hasPending = activeScans.length > 0;

  useEffect(() => {
    if (!hasPending) return;

    const authToken = resolveAuthToken(token);
    if (!authToken) return;

    // Only one interval across all mounted instances of this hook.
    // We increment the count first, then start the interval only if none is running.
    // IMPORTANT: every code path must return the SAME full cleanup so that
    // the interval is cleared regardless of which instance unmounts last.
    _pollCount++;
    if (!_pollInterval) {
      _pollInterval = setInterval(async () => {
        // Resolve the freshest token on every tick — avoids the stale-closure
        // problem where the interval captured an access token that has since
        // been silently refreshed and replaced in localStorage.
        const pollToken = getStoredToken();
        if (!pollToken) return;

        const current = queryClient.getQueryData<ActiveScan[]>(ACTIVE_SCANS_KEY) || [];
        if (current.length === 0) return;

        // Poll all active scans. Collect finished ones separately so we can
        // await the reports refetch BEFORE toasting — this ensures the report
        // row is already clickable when the notification appears.
        const outcomes: PollOutcome[] = await Promise.all(
          current.map(async (scan): Promise<PollOutcome> => {
            try {
              const result = await apiClient.getScan(scan.scanId, pollToken);
              if (result.status === 'completed' || result.status === 'failed') {
                markScanTerminal(scan.scanId);
                return { scan, nextState: null, finalStatus: result.status };
              }
              if (result.status !== scan.status) {
                return {
                  scan,
                  nextState: { ...scan, status: result.status as ActiveScan['status'] },
                  finalStatus: null,
                };
              }
            } catch {
              // keep current state on error
            }
            return { scan, nextState: scan, finalStatus: null };
          })
        );

        const finished = outcomes.filter((o) => o.nextState === null);
        const stillActive = outcomes
          .map((o) => o.nextState)
          .filter((s): s is ActiveScan => s !== null);

        saveToStorage(stillActive);
        queryClient.setQueryData(ACTIVE_SCANS_KEY, stillActive);

        if (finished.length > 0) {
          // A successful poll means the token is valid — cancel any pending
          // deferred logout that may have been queued by a prior transient error.
          cancelAuthFailure();

          // Await the reports refetch BEFORE toasting so the report row is
          // already in cache and clickable when the notification appears.
          // refetchQueries (vs invalidateQueries) issues the fetch immediately
          // and returns a promise that resolves once new data is in cache.
          await Promise.all([
            queryClient.refetchQueries({ queryKey: REPORTS_QUERY_KEYS.all }),
            queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] }),
          ]);

          // Reports list is now up to date — fire toasts.
          for (const { scan, finalStatus } of finished) {
            toast({
              title: finalStatus === 'completed' ? 'Scan complete' : 'Scan failed',
              description:
                finalStatus === 'completed'
                  ? `Results for ${scan.target} are now available.`
                  : `Scan of ${scan.target} failed. Check Reports for details.`,
            });
          }
        }
      }, 5000);
    }

    // Unified cleanup — runs for every mounted instance, not just the one
    // that created the interval. This ensures the interval is cleared even
    // if the creating instance unmounts before a later-joining instance.
    return () => {
      _pollCount--;
      if (_pollCount <= 0 && _pollInterval) {
        clearInterval(_pollInterval);
        _pollInterval = null;
        _pollCount = 0;
      }
    };
  }, [hasPending, token, queryClient]);

  return { activeScans, addScan, removeScan };
}
